import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import C from '../colors';
import { reportClientMonitorEvent } from '../clientMonitor';
import { getPictureThumbnailUrl } from '../pictureUrls';
import {
  isSameFolderName,
  matchLocalFiles,
  matchPicturesToLocalFiles,
  summarizeSourceFolders,
} from '../localFolderMatching';
import {
  getOriginalFile,
  loadOriginalSourcePlan,
  requestOriginalSourcePermissions,
  saveDirectorySource,
  saveFileSources,
} from '../localOriginalSources';

function getOutputFolderName(dirname) {
  const safeName = String(dirname || '')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  return safeName ? `已选-${safeName}` : '已选';
}

function getExportPickerId(albumId, orderRef) {
  const suffix = String(albumId || orderRef || 'pictures')
    .replace(/[^a-zA-Z0-9_-]/g, '-')
    .slice(0, 16);
  return `picsect-export-${suffix}`;
}

const OneClickOriginalExportBase = observer(({
  snackbarStore,
  pictures,
  dirname,
  orderRef,
  albumId,
}) => {
  const [sourcePlan, setSourcePlan] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(Boolean(albumId));
  const [sourceChecking, setSourceChecking] = useState(false);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [sourceItems, setSourceItems] = useState([]);
  const [repairingPictureId, setRepairingPictureId] = useState(null);
  const [exportWriting, setExportWriting] = useState(false);
  const sourceSummary = summarizeSourceFolders(pictures || []);
  const pictureIds = (pictures || []).map(picture => picture?.id).filter(Boolean).join(',');

  useEffect(() => {
    let active = true;
    if (!albumId || !pictures?.length) {
      setSourcePlan(null);
      setSourceLoading(false);
      return () => { active = false; };
    }
    setSourceLoading(true);
    loadOriginalSourcePlan({ albumId, pictures }).then(plan => {
      if (active) setSourcePlan(plan);
    }).catch(() => {
      if (active) setSourcePlan(null);
    }).finally(() => {
      if (active) setSourceLoading(false);
    });
    return () => { active = false; };
  // Picture ids capture the selection without reloading handles for unrelated object changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumId, pictureIds]);

  const showSourceItems = (plan, fileChecks) => {
    const items = plan.entries.map((entry, index) => ({
      entry,
      status: fileChecks[index].status === 'fulfilled' ? 'available' : 'missing',
      file: fileChecks[index].status === 'fulfilled' ? fileChecks[index].value : null,
    }));
    setSourceItems(items);
    setSourceDialogOpen(true);
    return items;
  };

  const reportValidatedSources = (plan) => {
    reportClientMonitorEvent('app_folder_match_completed', {
      page: 'web/photographer/local-organizer',
      orderRef,
      expectedPictureCount: (pictures || []).length,
      scannedFileCount: plan.entries.length,
      matchedFileCount: plan.entries.length,
      unmatchedPictureCount: 0,
      sourceKind: 'saved-handles',
      immediate: true,
    });
  };

  const openSavedSources = async () => {
    if (!sourcePlan?.oneClickEligible) return;
    setSourceChecking(true);
    try {
      await requestOriginalSourcePermissions(sourcePlan.entries);
      const checks = await Promise.allSettled(sourcePlan.entries.map(entry => getOriginalFile(entry)));
      const items = showSourceItems(sourcePlan, checks);
      const unavailableCount = items.filter(item => item.status === 'missing').length;
      if (unavailableCount > 0) {
        snackbarStore.showSnackbar(`有 ${unavailableCount} 张原片连接已断开，可以逐张重新连接`, 'warning');
        reportClientMonitorEvent('app_folder_source_check_failed', {
          page: 'web/photographer/local-organizer',
          orderRef,
          level: 'warn',
          expectedPictureCount: sourcePlan.entries.length,
          unavailablePictureCount: unavailableCount,
          sourceKind: 'saved-handles',
          immediate: true,
        });
      } else {
        reportValidatedSources(sourcePlan);
      }
    } finally {
      setSourceChecking(false);
    }
  };

  const relinkSinglePicture = async (item) => {
    if (!window.showOpenFilePicker || repairingPictureId) {
      if (!window.showOpenFilePicker) {
        snackbarStore.showSnackbar('当前浏览器无法逐张重新连接，请选择整个原片文件夹', 'warning');
      }
      return;
    }
    const pictureId = String(item.entry.binding.pictureId);
    setRepairingPictureId(pictureId);
    try {
      const [fileHandle] = await window.showOpenFilePicker({ multiple: false });
      if (!fileHandle) return;
      const file = await fileHandle.getFile();
      await saveFileSources({
        albumId,
        bindings: [{ pictureId, fileName: file.name, fileHandle }],
        oneClickEligible: true,
      });
      const refreshedPlan = await loadOriginalSourcePlan({ albumId, pictures });
      setSourcePlan(refreshedPlan);
      const refreshedEntry = refreshedPlan.entries.find(
        entry => String(entry.binding.pictureId) === pictureId
      );
      const nextItems = sourceItems.map(sourceItem => (
        String(sourceItem.entry.binding.pictureId) === pictureId
          ? { entry: refreshedEntry || sourceItem.entry, status: 'available', file }
          : sourceItem
      ));
      setSourceItems(nextItems);
      if (nextItems.every(sourceItem => sourceItem.status === 'available')) {
        snackbarStore.showSnackbar('原片已全部连接，可以选择写入文件夹', 'success');
        reportValidatedSources(refreshedPlan);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        snackbarStore.showSnackbar('重新连接这张原片失败，请重试', 'warning');
      }
    } finally {
      setRepairingPictureId(null);
    }
  };

  const exportConnectedOriginals = async () => {
    if (exportWriting || sourceItems.some(item => item.status !== 'available')) return;
    setExportWriting(true);
    let failureStage = 'open-destination';
    try {
      const outputRoot = await window.showDirectoryPicker({
        id: getExportPickerId(albumId, orderRef),
        mode: 'readwrite',
      });
      const outputFolderName = getOutputFolderName(dirname);
      failureStage = 'create-output-folder';
      const outputDirectory = await outputRoot.getDirectoryHandle(outputFolderName, { create: true });
      for (const item of sourceItems) {
        failureStage = 'write-destination';
        const outputName = item.file?.name || item.entry.binding.fileName;
        const outputFileHandle = await outputDirectory.getFileHandle(outputName, { create: true });
        const writable = await outputFileHandle.createWritable();
        await writable.write(item.file);
        await writable.close();
      }
      reportClientMonitorEvent('app_folder_write_completed', {
        page: 'web/photographer/local-organizer',
        orderRef,
        requestedFileCount: sourceItems.length,
        writtenFileCount: sourceItems.length,
        sourceKind: 'saved-handles',
        immediate: true,
      });
      snackbarStore.showSnackbar(`已将 ${sourceItems.length} 个原片导出到「${outputFolderName}」`, 'success');
      setSourceDialogOpen(false);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      reportClientMonitorEvent('app_folder_write_failed', {
        page: 'web/photographer/local-organizer',
        orderRef,
        level: 'error',
        requestedFileCount: sourceItems.length,
        failureKind: String(error?.name || 'unknown').slice(0, 80),
        failureStage,
        sourceKind: 'saved-handles',
        immediate: true,
      });
      snackbarStore.showSnackbar(
        failureStage === 'open-destination'
          ? '无法打开保存位置，请重试或更换浏览器'
          : '写入原片失败，请换一个保存位置重试',
        'warning'
      );
    } finally {
      setExportWriting(false);
    }
  };

  const reconnectFolder = async () => {
    if (!window.showDirectoryPicker) {
      snackbarStore.showSnackbar('当前浏览器不支持文件夹整理，请使用最新版 Chrome 或 Edge', 'warning');
      return;
    }
    try {
      const directoryHandle = await window.showDirectoryPicker({ mode: 'read' });
      const files = [];
      let ignoredDirectoryCount = 0;
      for await (const entry of directoryHandle.values()) {
        if (entry.kind === 'file') files.push(entry.name);
        else if (entry.kind === 'directory') ignoredDirectoryCount += 1;
      }
      const matchResult = matchLocalFiles(pictures, files);
      const bindings = matchPicturesToLocalFiles(pictures, files);
      let refreshedPlan = null;
      if (albumId && bindings.length > 0) {
        try {
          await saveDirectorySource({
            albumId,
            directoryHandle,
            bindings,
            oneClickEligible: true,
          });
          refreshedPlan = await loadOriginalSourcePlan({ albumId, pictures });
          setSourcePlan(refreshedPlan);
        } catch (_) {
          snackbarStore.showSnackbar('本次可以继续导出，但未能保存原片关联', 'warning');
        }
      }
      reportClientMonitorEvent('app_folder_match_completed', {
        page: 'web/photographer/local-organizer',
        orderRef,
        expectedPictureCount: (pictures || []).length,
        scannedFileCount: files.length,
        matchedFileCount: matchResult.selected.length,
        unmatchedPictureCount: matchResult.unmatchedPictureCount,
        ignoredDirectoryCount,
        knownSourceFolderCount: sourceSummary.knownCount,
        sourceFolderVariantCount: sourceSummary.folders.length,
        sourceFolderNameMatched: sourceSummary.recommended
          ? isSameFolderName(directoryHandle.name, sourceSummary.recommended.name)
          : null,
        sourceKind: 'relinked-directory',
        immediate: true,
      });
      if (refreshedPlan) {
        const checks = await Promise.allSettled(refreshedPlan.entries.map(entry => getOriginalFile(entry)));
        const items = showSourceItems(refreshedPlan, checks);
        if (items.every(item => item.status === 'available')) reportValidatedSources(refreshedPlan);
      }
    } catch (error) {
      if (error.code !== 20 && error.name !== 'AbortError') {
        reportClientMonitorEvent('app_folder_open_failed', {
          page: 'web/photographer/local-organizer',
          orderRef,
          level: 'error',
          failureKind: String(error?.name || 'unknown').slice(0, 80),
          immediate: true,
        });
        snackbarStore.showSnackbar('打开本地文件夹失败', 'warning');
      }
    }
  };

  if (!pictures) return null;
  const missingCount = sourceItems.filter(item => item.status === 'missing').length;
  const allAvailable = sourceItems.length > 0 && missingCount === 0;

  return (
    <div>
      {sourcePlan?.oneClickEligible && (
        <button
          type="button"
          data-testid="one-click-export-originals"
          disabled={!pictures.length || sourceLoading || sourceChecking}
          onClick={openSavedSources}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 11px 8px 13px', borderRadius: 9,
            border: '1px solid rgba(217,79,146,.32)',
            background: 'rgba(247,119,176,.09)', color: C.brand2,
            fontFamily: C.sans, fontSize: 12.5, fontWeight: 600,
            cursor: pictures.length && !sourceLoading && !sourceChecking ? 'pointer' : 'not-allowed',
            whiteSpace: 'nowrap',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 20h14"/>
          </svg>
          {sourceChecking ? '正在检查原片…' : '一键导出原片'}
          <span style={{
            padding: '2px 5px', borderRadius: 5, background: C.brand, color: '#fff',
            fontFamily: C.mono, fontSize: 8, fontWeight: 700, letterSpacing: .7,
          }}>BETA</span>
        </button>
      )}

      {sourceDialogOpen && (
        <div
          data-testid="source-repair-dialog"
          onClick={() => setSourceDialogOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1195,
            background: 'rgba(26,20,23,.56)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
        >
          <div onClick={event => event.stopPropagation()} style={{
            width: '100%', maxWidth: 620, maxHeight: '82vh', overflow: 'hidden',
            borderRadius: 18, background: C.paper,
            boxShadow: '0 28px 70px -24px rgba(26,20,23,.5)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: '24px 26px 17px', borderBottom: `1px solid ${C.hair}` }}>
              <div style={{ color: C.brand, fontSize: 10, fontWeight: 700, letterSpacing: 1.5 }}>
                BETA · 原片连接检查
              </div>
              <h3 style={{ margin: '8px 0 0', fontFamily: C.serif, fontSize: 27, lineHeight: 1.15, fontWeight: 400, color: C.ink }}>
                原片连接情况
              </h3>
              <p style={{ margin: '10px 0 0', fontFamily: C.sans, fontSize: 12.5, lineHeight: 1.65, color: C.ink2 }}>
                {missingCount > 0
                  ? '点击带有断链标记的照片，可以只重新选择并连接这一张原片。'
                  : '所有已选照片均已连接，可以直接选择写入文件夹并导出。'}
              </p>
            </div>

            <div style={{ padding: '15px 20px 18px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, color: C.ink3, fontSize: 11.5 }}>
                <span>{sourceItems.length - missingCount}/{sourceItems.length} 已连接</span>
                <span style={{ color: C.hair2 }}>·</span>
                <strong style={{ color: missingCount ? '#A53636' : '#2F6F49', fontWeight: 600 }}>
                  {missingCount ? `${missingCount} 张需要重新连接` : '全部连接正常'}
                </strong>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 10 }}>
                {sourceItems.map(item => {
                  const pictureId = String(item.entry.binding.pictureId);
                  const missing = item.status === 'missing';
                  const repairing = repairingPictureId === pictureId;
                  return (
                    <button
                      key={pictureId}
                      type="button"
                      data-testid={`source-repair-picture-${pictureId}`}
                      disabled={!missing || Boolean(repairingPictureId)}
                      onClick={() => relinkSinglePicture(item)}
                      style={{
                        padding: 0, overflow: 'hidden', textAlign: 'left', borderRadius: 11,
                        border: `1px solid ${missing ? '#D98C87' : '#B9DFC8'}`,
                        background: '#fff', color: C.ink,
                        cursor: missing && !repairingPictureId ? 'pointer' : 'default',
                        opacity: repairingPictureId && !repairing ? .58 : 1,
                      }}
                    >
                      <div style={{ position: 'relative', aspectRatio: '4 / 3', overflow: 'hidden', background: C.paper2 }}>
                        <img
                          src={getPictureThumbnailUrl(item.entry.picture)}
                          alt={item.entry.binding.fileName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        {missing ? (
                          <div data-testid={`source-broken-${pictureId}`} style={{
                            position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                            background: 'rgba(72,30,36,.72)', color: '#fff', textAlign: 'center', padding: 8,
                          }}>
                            <div>
                              <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 5px' }}>
                                <path d="m10.5 13.5 3-3"/><path d="m7.5 16.5-1 1a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0"/><path d="m16.5 7.5 1-1a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0"/><path d="m3 3 18 18"/>
                              </svg>
                              <div style={{ fontSize: 10.5, fontWeight: 700 }}>{repairing ? '正在重新连接…' : '连接已断开'}</div>
                              {!repairing && <div style={{ marginTop: 2, fontSize: 9, opacity: .82 }}>点击重新连接</div>}
                            </div>
                          </div>
                        ) : (
                          <div style={{ position: 'absolute', right: 7, top: 7, padding: '3px 6px', borderRadius: 8, background: 'rgba(36,104,67,.88)', color: '#fff', fontSize: 9, fontWeight: 700 }}>
                            已连接
                          </div>
                        )}
                      </div>
                      <div title={item.entry.binding.fileName} style={{ padding: '8px 9px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: C.mono, fontSize: 10.5 }}>
                        {item.entry.binding.fileName}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ padding: '13px 20px 18px', borderTop: `1px solid ${C.hair}`, display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
              <button type="button" onClick={() => setSourceDialogOpen(false)} style={{
                padding: '9px 14px', borderRadius: 9, border: `1px solid ${C.hair2}`,
                background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 12.5, cursor: 'pointer',
              }}>关闭</button>
              {missingCount > 0 && (
                <button type="button" data-testid="repair-from-folder" onClick={reconnectFolder} style={{
                  padding: '9px 14px', borderRadius: 9, border: `1px solid ${C.hair2}`,
                  background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                }}>重新选择整个文件夹</button>
              )}
              <button
                type="button"
                data-testid="export-connected-originals"
                disabled={exportWriting || !allAvailable}
                onClick={exportConnectedOriginals}
                style={{
                  padding: '10px 16px', borderRadius: 9, border: 'none',
                  background: !exportWriting && allAvailable ? C.ink : C.hair2,
                  color: '#fff', fontFamily: C.sans, fontSize: 12.5, fontWeight: 600,
                  cursor: !exportWriting && allAvailable ? 'pointer' : 'not-allowed',
                }}
              >
                {exportWriting
                  ? '正在写入…'
                  : (missingCount
                    ? `还有 ${missingCount} 张未连接`
                    : `选择写入文件夹并导出 · ${sourceItems.length} 张`)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const OneClickOriginalExport = inject('snackbarStore')(OneClickOriginalExportBase);

export { getExportPickerId, OneClickOriginalExportBase };
export default OneClickOriginalExport;
